<template>
  <div class="admin-page monitor-page" v-loading="loading">
    <!-- ============ 页头 ============ -->
    <div class="page-head">
      <div class="head-text">
        <h1>系统监控</h1>
        <p>
          近 {{ windowMinutes }} 分钟 ·
          <span class="tag tag--super"><i class="el-icon-lock" />仅超管可见</span>
          <span v-if="!overview.rateLimitEnabled" class="tag tag--off">限流已关闭</span>
        </p>
      </div>
      <div class="head-actions">
        <div class="status-tabs">
          <button
            v-for="m in [5, 30, 60, 240]"
            :key="m"
            type="button"
            class="tab"
            :class="{ 'is-active': windowMinutes === m }"
            @click="switchWindow(m)"
          >
            {{ m < 60 ? m + ' 分钟' : (m / 60) + ' 小时' }}
          </button>
        </div>
        <button type="button" class="btn" @click="refreshAll">
          <i class="el-icon-refresh" />刷新
        </button>
      </div>
    </div>

    <!-- ============ 我的 IP（做白名单时唯一安全的取值方式） ============ -->
    <div class="my-ip" v-if="myIp">
      <div class="my-ip__label">
        <i class="el-icon-position" />
        你的当前 IP
      </div>
      <div class="my-ip__value">{{ myIp.ip }}</div>
      <div class="my-ip__tags">
        <span v-if="myIp.localOrPrivate" class="chip">内网/本机 · 永久放行</span>
        <span v-if="myIp.inWhitelist" class="chip chip--ok">已在后台白名单</span>
        <span v-else class="chip chip--warn">不在后台白名单</span>
        <span v-if="myIp.fromTrustedProxy" class="chip">经可信代理转发</span>
      </div>
      <button
        v-if="!myIp.inWhitelist && !myIp.localOrPrivate"
        type="button"
        class="btn btn--primary"
        @click="addMyIp"
      >
        加入后台白名单
      </button>
    </div>

    <!-- ============ 四个指标 ============ -->
    <div class="kpis">
      <div class="kpi">
        <div class="k">QPS</div>
        <div class="v num">{{ num(overview.qps) }}</div>
        <div class="s">总请求 {{ num(overview.total) }}</div>
      </div>
      <div class="kpi" :class="{ 'kpi--warn': overview.serverError > 0 }">
        <div class="k">服务端错误</div>
        <div class="v num">{{ num(overview.serverError) }}</div>
        <div class="s">错误率 {{ percentText(overview.errorRate) }}（只算 5xx）</div>
      </div>
      <div class="kpi" :class="{ 'kpi--warn': overview.authError > 20 }">
        <div class="k">401 / 403</div>
        <div class="v num">{{ num(overview.authError) }}</div>
        <div class="s">越权探测的主要信号</div>
      </div>
      <div class="kpi" :class="{ 'kpi--warn': overview.avgMs > 800 }">
        <div class="k">平均耗时</div>
        <div class="v num">{{ num(overview.avgMs) }}<small>ms</small></div>
        <div class="s">最慢 {{ num(overview.maxMs) }} ms</div>
      </div>
    </div>

    <!-- ============ 接口排行 ============ -->
    <div class="m-card">
      <div class="m-card__head">
        <div class="m-card__title">接口排行</div>
        <div class="status-tabs">
          <button
            v-for="opt in apiSorts"
            :key="opt.value"
            type="button"
            class="tab"
            :class="{ 'is-active': apiSort === opt.value }"
            @click="switchApiSort(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <div v-if="!apis.length" class="m-empty">
        <i class="el-icon-data-line" />
        <p>这个时间窗内没有接口指标</p>
        <span>指标每 30 秒落库一次，刚重启的话等一会儿就有了</span>
      </div>
      <table v-else class="m-table">
        <thead>
          <tr>
            <th>接口</th>
            <th>调用量</th>
            <th>平均耗时</th>
            <th>最慢</th>
            <th>5xx</th>
            <th>401/403</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in apis" :key="item.method + item.path">
            <td>
              <div class="api-cell">
                <span class="api-cell__method" :class="'m-' + item.method">{{ item.method }}</span>
                <span class="api-cell__path">{{ item.path }}</span>
              </div>
            </td>
            <td class="num">{{ num(item.total) }}</td>
            <td class="num" :class="{ 'is-warn': item.avgMs > 800 }">{{ num(item.avgMs) }} ms</td>
            <td class="num" :class="{ 'is-warn': item.maxMs > 3000 }">{{ num(item.maxMs) }} ms</td>
            <td class="num" :class="{ 'is-danger': item.serverError > 0 }">{{ num(item.serverError) }}</td>
            <td class="num" :class="{ 'is-warn': item.authError > 0 }">{{ num(item.authError) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============ 安全事件 + IP 规则 ============ -->
    <div class="m-cols">
      <div class="m-card" style="margin-top: 0">
        <div class="m-card__head">
          <div class="m-card__title">安全事件</div>
          <span class="m-card__hint">
            自动封禁中 {{ num(overview.autoBannedCount) }} 个 IP
          </span>
        </div>
        <div v-if="!events.length" class="m-empty m-empty--sm">
          <i class="el-icon-circle-check" />
          <p>没有安全事件</p>
        </div>
        <div v-else class="event-list">
          <div class="event" v-for="item in events" :key="String(item.id)">
            <span class="event__pill" :class="'t-' + item.type">{{ item.typeLabel }}</span>
            <div class="event__body">
              <div class="event__line">
                <span class="event__subject">{{ item.subject }}</span>
                <span class="event__hit">×{{ item.hitCount }}</span>
              </div>
              <div class="event__meta">
                <span>{{ item.path || '—' }}</span>
                <span>{{ relativeTime(item.lastAt) }}</span>
              </div>
              <div class="event__detail" v-if="item.detail">{{ item.detail }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="m-card" style="margin-top: 0">
        <div class="m-card__head">
          <div class="m-card__title">IP 黑白名单</div>
          <button type="button" class="link-btn" @click="openAddRule">+ 添加</button>
        </div>
        <div class="ip-warn" v-if="overview.adminWhitelistEnabled">
          <i class="el-icon-warning" />
          后台 IP 白名单<b>已开启</b>：只有白名单里的 IP 能访问管理端。
          ⛔ 加新 IP 前先确认自己当前 IP 已在列表里，否则会把自己锁在外面。
        </div>
        <div v-if="!ipRules.length" class="m-empty m-empty--sm">
          <i class="el-icon-unlock" />
          <p>还没有任何 IP 规则</p>
          <span>白名单关闭时，所有 IP 都可以访问后台</span>
        </div>
        <div v-else class="rule-list">
          <div class="rule" v-for="item in ipRules" :key="String(item.id)">
            <span class="rule__ip">{{ item.ip }}</span>
            <span class="rule__tag" :class="item.rule === 'ALLOW' ? 'is-allow' : 'is-deny'">
              {{ item.rule === 'ALLOW' ? '放行' : '拒绝' }}
            </span>
            <span class="rule__scope">{{ scopeLabel(item.scope) }}</span>
            <span class="rule__meta">
              <template v-if="item.auto">自动 · 剩 {{ item.remainMinutes }} 分钟</template>
              <template v-else-if="item.expireAt">至 {{ formatTime(item.expireAt) }}</template>
              <template v-else>永久</template>
            </span>
            <button type="button" class="rule__del" title="删除" @click="removeRule(item)">
              <i class="el-icon-close" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ 审计日志 ============ -->
    <div class="m-card">
      <div class="m-card__head">
        <div class="m-card__title">操作审计</div>
        <div class="audit-filter">
          <el-input
            v-model="auditQuery.path"
            size="small"
            class="filter-input"
            placeholder="按接口路径筛选，如 /ban"
            clearable
            @clear="searchAudit"
            @keyup.enter.native="searchAudit"
          />
          <el-select v-model="auditQuery.days" size="small" class="filter-days" @change="searchAudit">
            <el-option label="近 1 天" :value="1" />
            <el-option label="近 7 天" :value="7" />
            <el-option label="近 30 天" :value="30" />
          </el-select>
        </div>
      </div>
      <div v-if="!audits.length" class="m-empty m-empty--sm">
        <i class="el-icon-document" />
        <p>没有审计记录</p>
        <span>所有非查询类后台操作都会自动留痕（密码等字段已脱敏）</span>
      </div>
      <template v-else>
        <table class="m-table">
          <thead>
            <tr>
              <th>时间</th>
              <th>操作人</th>
              <th>接口</th>
              <th>结果</th>
              <th>耗时</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in audits" :key="String(item.id)" class="is-clickable" @click="openAuditDetail(item)">
              <td class="num time">{{ formatTime(item.createdAt) }}</td>
              <td>
                <span class="cell-user">{{ item.adminUsername || '#' + item.adminId }}</span>
                <span class="cell-role">{{ roleLabel(item.role) }}</span>
              </td>
              <td>
                <div class="api-cell">
                  <span class="api-cell__method" :class="'m-' + item.method">{{ item.method }}</span>
                  <span class="api-cell__path">{{ item.path }}</span>
                </div>
              </td>
              <td>
                <span class="pill" :class="item.ok === 1 ? 'pill--ok' : 'pill--fail'">
                  {{ item.ok === 1 ? '成功' : '失败' }}
                </span>
                <span class="cell-status">{{ item.status }}</span>
              </td>
              <td class="num">{{ num(item.costMs) }} ms</td>
              <td class="num time">{{ item.ip }}</td>
            </tr>
          </tbody>
        </table>
        <div class="m-pager">
          <span>共 {{ auditTotal }} 条</span>
          <el-pagination
            background
            small
            layout="prev, pager, next"
            :current-page="auditQuery.page"
            :page-size="auditQuery.size"
            :total="auditTotal"
            @current-change="changeAuditPage"
          />
        </div>
      </template>
    </div>

    <!-- ============ 审计详情抽屉 ============ -->
    <el-drawer :visible.sync="auditDetailVisible" :with-header="false" size="640px" class="admin-drawer">
      <div class="drawer-body">
        <div class="drawer-head">
          <div>
            <h3>{{ auditDetail.path }}</h3>
            <p>
              {{ auditDetail.method }} · {{ auditDetail.adminUsername }}
              （{{ roleLabel(auditDetail.role) }}）· {{ formatTime(auditDetail.createdAt) }}
            </p>
          </div>
          <button type="button" class="drawer-close" @click="auditDetailVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>
        <div class="detail-block">
          <div class="detail-block__label">请求参数（已脱敏）</div>
          <pre class="detail-block__code">{{ auditDetail.body || '（无请求体）' }}</pre>
        </div>
        <div class="detail-block" v-if="auditDetail.query">
          <div class="detail-block__label">查询串</div>
          <pre class="detail-block__code">{{ auditDetail.query }}</pre>
        </div>
        <div class="detail-meta">
          <span>状态码 {{ auditDetail.status }}</span>
          <span>耗时 {{ num(auditDetail.costMs) }} ms</span>
          <span>IP {{ auditDetail.ip }}</span>
        </div>
      </div>
    </el-drawer>

    <!-- ============ 添加 IP 规则 ============ -->
    <el-dialog title="添加 IP 规则" :visible.sync="ruleDialogVisible" width="440px">
      <el-form label-width="82px" size="small">
        <el-form-item label="IP">
          <el-input v-model="ruleForm.ip" placeholder="精确 IP 或前缀，如 203.0.113." />
          <div class="form-hint">前缀写法（以点结尾）匹配整段，不用填掩码</div>
        </el-form-item>
        <el-form-item label="作用域">
          <el-select v-model="ruleForm.scope" style="width: 100%">
            <el-option label="后台管理端（ADMIN）" value="ADMIN" />
            <el-option label="小程序接口（APP）" value="APP" />
            <el-option label="全部（ALL）" value="ALL" />
          </el-select>
        </el-form-item>
        <el-form-item label="规则">
          <el-radio-group v-model="ruleForm.rule">
            <el-radio label="ALLOW">放行（白名单）</el-radio>
            <el-radio label="DENY">拒绝（黑名单）</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="ruleForm.reason" placeholder="写清为什么要加这条（以后自己看得懂）" />
        </el-form-item>
      </el-form>
      <div class="dialog-tip">
        优先级：手工拒绝 &gt; 自动封禁 &gt; 白名单放行。白名单只豁免自动封禁，不豁免手工拒绝。
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="ruleDialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="submitRule">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMonitorOverview,
  getMonitorApis,
  getMonitorEvents,
  getIpRules,
  addIpRule,
  deleteIpRule,
  getMyIp,
  getAuditLogs
} from '@/api/monitor'

export default {
  name: 'SystemMonitor',
  data() {
    return {
      loading: false,
      windowMinutes: 30,
      overview: {},
      apis: [],
      apiSort: 'slow',
      apiSorts: [
        { label: '最慢', value: 'slow' },
        { label: '报错最多', value: 'error' },
        { label: '调用最多', value: 'total' }
      ],
      events: [],
      ipRules: [],
      myIp: null,
      audits: [],
      auditTotal: 0,
      auditQuery: { page: 1, size: 20, path: '', days: 7 },
      auditDetailVisible: false,
      auditDetail: {},
      ruleDialogVisible: false,
      ruleForm: { ip: '', scope: 'ADMIN', rule: 'ALLOW', reason: '' }
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    refreshAll() {
      this.loading = true
      Promise.all([
        this.loadOverview(),
        this.loadApis(),
        this.loadEvents(),
        this.loadRules(),
        this.loadMyIp(),
        this.loadAudits()
      ]).then(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    loadOverview() {
      return getMonitorOverview({ minutes: this.windowMinutes }).then(res => {
        this.overview = (res && res.data) || {}
      })
    },

    loadApis() {
      return getMonitorApis({
        minutes: this.windowMinutes,
        sortBy: this.apiSort,
        limit: 15
      }).then(res => {
        this.apis = (res && res.data) || []
      })
    },

    loadEvents() {
      return getMonitorEvents({ limit: 50 }).then(res => {
        this.events = (res && res.data) || []
      })
    },

    loadRules() {
      return getIpRules().then(res => {
        this.ipRules = (res && res.data) || []
      })
    },

    loadMyIp() {
      return getMyIp().then(res => {
        this.myIp = (res && res.data) || null
      })
    },

    loadAudits() {
      return getAuditLogs({
        page: this.auditQuery.page,
        size: this.auditQuery.size,
        path: this.auditQuery.path || undefined,
        days: this.auditQuery.days
      }).then(res => {
        const data = (res && res.data) || {}
        this.audits = data.list || []
        this.auditTotal = data.total || 0
      })
    },

    switchWindow(m) {
      if (this.windowMinutes === m) {
        return
      }
      this.windowMinutes = m
      this.loadOverview()
      this.loadApis()
    },

    switchApiSort(value) {
      if (this.apiSort === value) {
        return
      }
      this.apiSort = value
      this.loadApis()
    },

    searchAudit() {
      this.auditQuery.page = 1
      this.loadAudits()
    },

    changeAuditPage(page) {
      this.auditQuery.page = page
      this.loadAudits()
    },

    openAuditDetail(item) {
      this.auditDetail = item
      this.auditDetailVisible = true
    },

    /** 一键把当前 IP 加进后台白名单（配白名单唯一安全的路径） */
    addMyIp() {
      if (!this.myIp || !this.myIp.ip) {
        return
      }
      this.$confirm(
        `把 ${this.myIp.ip} 加入后台白名单？\n加完再打开白名单开关，否则会把自己锁在外面。`,
        '加入白名单',
        { type: 'info' }
      ).then(() => addIpRule({
        ip: this.myIp.ip,
        scope: 'ADMIN',
        rule: 'ALLOW',
        reason: '超管把自己当前 IP 加白（系统监控页一键添加）'
      })).then(() => {
        this.$message.success('已加入白名单')
        this.loadRules()
        this.loadMyIp()
      }).catch(() => {})
    },

    openAddRule() {
      this.ruleForm = { ip: '', scope: 'ADMIN', rule: 'ALLOW', reason: '' }
      this.ruleDialogVisible = true
    },

    submitRule() {
      if (!this.ruleForm.ip || !this.ruleForm.ip.trim()) {
        this.$message.warning('请填写 IP')
        return
      }
      if (this.ruleForm.rule === 'DENY' && this.ruleForm.scope === 'ADMIN') {
        // ⛔ 拉黑后台作用域是个高危动作：可能把同事/自己一起关在外面
        this.$confirm(
          `确认把 ${this.ruleForm.ip} 加入后台黑名单？\n如果这是你自己的 IP，你会立刻无法访问管理端。`,
          '高危操作',
          { type: 'warning', confirmButtonText: '确认拉黑' }
        ).then(() => this.doSaveRule()).catch(() => {})
        return
      }
      this.doSaveRule()
    },

    doSaveRule() {
      addIpRule({
        ip: this.ruleForm.ip.trim(),
        scope: this.ruleForm.scope,
        rule: this.ruleForm.rule,
        reason: this.ruleForm.reason
      }).then(() => {
        this.$message.success('已保存，5 秒内生效')
        this.ruleDialogVisible = false
        this.loadRules()
      }).catch(() => {})
    },

    removeRule(item) {
      this.$confirm(`删除 IP 规则 ${item.ip}？`, '删除规则', { type: 'warning' })
        .then(() => deleteIpRule(item.id))
        .then(() => {
          this.$message.success('已删除')
          this.loadRules()
        }).catch(() => {})
    },

    /* ---------------- 格式化 ---------------- */

    num(value) {
      const n = Number(value)
      if (!isFinite(n)) {
        return '0'
      }
      return n.toLocaleString('zh-CN')
    },

    percentText(value) {
      const n = Number(value)
      if (!isFinite(n) || n <= 0) {
        return '0%'
      }
      return (n * 100).toFixed(2) + '%'
    },

    formatTime(value) {
      if (!value) {
        return ''
      }
      return String(value).replace('T', ' ').slice(0, 19)
    },

    /** 相对时间：安全事件看"刚才还是三小时前"比看绝对时间更有用 */
    relativeTime(value) {
      if (!value) {
        return ''
      }
      const time = new Date(String(value).replace(' ', 'T')).getTime()
      if (!isFinite(time)) {
        return this.formatTime(value)
      }
      const diff = Date.now() - time
      if (diff < 60_000) {
        return '刚刚'
      }
      if (diff < 3_600_000) {
        return Math.floor(diff / 60_000) + ' 分钟前'
      }
      if (diff < 86_400_000) {
        return Math.floor(diff / 3_600_000) + ' 小时前'
      }
      return Math.floor(diff / 86_400_000) + ' 天前'
    },

    scopeLabel(scope) {
      const map = { ADMIN: '后台', APP: '小程序', ALL: '全部' }
      return map[scope] || scope
    },

    roleLabel(role) {
      const map = { super: '超管', admin: '管理员', operator: '运营' }
      return map[role] || role || '—'
    }
  }
}
</script>

<style lang="scss" scoped>
/* ---------- 页头 ---------- */
.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 19px;
  padding: 0 7px;
  margin-left: 8px;
  border-radius: 4px;
  font-size: 11px;

  i {
    font-size: 10px;
  }

  &--super {
    background: var(--rose-50);
    color: var(--rose-600);
    border: 1px solid #f6d6da;
  }

  &--off {
    background: var(--warn-bg);
    color: var(--warn-text);
    border: 1px solid #f0e3c4;
  }
}

/* ---------- 我的 IP（做白名单时唯一安全的取值方式） ---------- */
.my-ip {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 18px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-card);

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--ink-400);

    i {
      font-size: 13px;
    }
  }

  &__value {
    font-size: 18px;
    font-weight: 600;
    color: var(--ink-900);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.3px;
  }

  &__tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.chip {
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--ink-100);
  color: var(--ink-500);

  &--ok {
    background: var(--ok-bg);
    color: var(--ok-text);
  }

  &--warn {
    background: var(--warn-bg);
    color: var(--warn-text);
  }
}

/* ---------- 四个指标 ---------- */
.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}

.kpi {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 15px 18px;
  box-shadow: var(--sh-card);

  .k {
    font-size: 12px;
    color: var(--ink-400);
  }

  .v {
    margin-top: 8px;
    font-size: 26px;
    font-weight: 600;
    line-height: 1;
    color: var(--ink-900);
    font-variant-numeric: tabular-nums;

    small {
      font-size: 12px;
      font-weight: 400;
      color: var(--ink-400);
      margin-left: 3px;
    }
  }

  .s {
    margin-top: 8px;
    font-size: 11px;
    color: var(--ink-400);
  }

  /* 只有出问题才染色：正常状态保持中性，否则满屏红黄等于没有重点 */
  &--warn .v {
    color: var(--coral);
  }
}

/* ---------- 卡片 ---------- */
.m-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-card);
  margin-top: 14px;
  margin-bottom: 14px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--ink-100);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__hint {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.m-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 14px;
}

/* ---------- 空态 ---------- */
.m-empty {
  padding: 42px 20px;
  text-align: center;

  i {
    font-size: 26px;
    color: var(--ink-300);
  }

  p {
    margin-top: 9px;
    font-size: 13px;
    color: var(--ink-500);
  }

  span {
    display: block;
    margin-top: 5px;
    font-size: 11px;
    color: var(--ink-400);
  }

  &--sm {
    padding: 28px 20px;
  }
}

/* ---------- 表格 ---------- */
.m-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 10px 18px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ink-400);
    text-align: right;
    background: #fdfbfa;
    white-space: nowrap;

    &:first-child {
      text-align: left;
    }
  }

  td {
    padding: 12px 18px;
    font-size: 13px;
    color: var(--ink-600);
    text-align: right;
    border-top: 1px solid var(--ink-100);

    &:first-child {
      text-align: left;
    }

    &.num {
      font-variant-numeric: tabular-nums;
    }

    &.time {
      font-size: 12px;
      color: var(--ink-400);
    }

    &.is-warn {
      color: var(--warn-text);
    }

    &.is-danger {
      color: var(--danger);
      font-weight: 600;
    }
  }

  tbody tr.is-clickable {
    cursor: pointer;

    &:hover td {
      background: #fdf7f5;
    }
  }
}

.api-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  &__method {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 3px;
    letter-spacing: 0.4px;
    background: var(--ink-100);
    color: var(--ink-500);

    &.m-GET {
      background: #eef3fa;
      color: #3a5a8c;
    }

    &.m-POST {
      background: var(--ok-bg);
      color: var(--ok-text);
    }

    &.m-PUT,
    &.m-PATCH {
      background: var(--warn-bg);
      color: var(--warn-text);
    }

    &.m-DELETE {
      background: var(--danger-bg);
      color: var(--danger);
    }
  }

  &__path {
    font-size: 12px;
    color: var(--ink-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.cell-user {
  font-weight: 500;
  color: var(--ink-900);
}

.cell-role {
  margin-left: 6px;
  font-size: 11px;
  color: var(--ink-400);
}

.cell-status {
  margin-left: 6px;
  font-size: 11px;
  color: var(--ink-400);
  font-variant-numeric: tabular-nums;
}

.pill {
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 4px;

  &--ok {
    background: var(--ok-bg);
    color: var(--ok-text);
  }

  &--fail {
    background: var(--danger-bg);
    color: var(--danger);
  }
}

/* ---------- 安全事件 ---------- */
.event-list {
  max-height: 380px;
  overflow-y: auto;
  padding: 6px 20px 12px;
}

.event {
  display: flex;
  gap: 10px;
  padding: 11px 0;
  border-bottom: 1px dashed var(--ink-100);

  &:last-child {
    border-bottom: none;
  }

  &__pill {
    flex-shrink: 0;
    height: 21px;
    line-height: 21px;
    padding: 0 7px;
    border-radius: 4px;
    font-size: 11px;
    background: var(--ink-100);
    color: var(--ink-500);

    /* 只有"真的危险"才染红：限流是常态，不该满屏红 */
    &.t-AUTH_BRUTE,
    &.t-ADMIN_IP_NOT_ALLOWED {
      background: var(--danger-bg);
      color: var(--danger);
    }

    &.t-RATE_LIMIT,
    &.t-IP_DENIED {
      background: var(--warn-bg);
      color: var(--warn-text);
    }

    &.t-AD_SUSPECT {
      background: var(--warn-bg);
      color: var(--warn-text);
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  &__subject {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-900);
    font-variant-numeric: tabular-nums;
  }

  &__hit {
    font-size: 12px;
    color: var(--ink-600);
    font-variant-numeric: tabular-nums;
  }

  &__meta {
    display: flex;
    gap: 12px;
    margin-top: 4px;

    span {
      font-size: 11px;
      color: var(--ink-400);
    }
  }

  &__detail {
    margin-top: 4px;
    font-size: 11px;
    color: var(--ink-500);
    line-height: 1.6;
  }
}

/* ---------- IP 规则 ---------- */
.ip-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 20px 0;
  padding: 10px 12px;
  background: var(--warn-bg);
  border: 1px solid #f0e3c4;
  border-radius: var(--r-md);
  font-size: 11px;
  color: var(--warn-text);
  line-height: 1.7;

  i {
    margin-top: 2px;
    flex-shrink: 0;
  }

  b {
    font-weight: 600;
  }
}

.rule-list {
  max-height: 340px;
  overflow-y: auto;
  padding: 6px 20px 12px;
}

.rule {
  display: grid;
  grid-template-columns: 1fr auto auto auto 24px;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px dashed var(--ink-100);

  &:last-child {
    border-bottom: none;
  }

  &__ip {
    font-size: 13px;
    color: var(--ink-900);
    font-variant-numeric: tabular-nums;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__tag {
    font-size: 11px;
    padding: 1px 6px;
    border-radius: 3px;

    &.is-allow {
      background: var(--ok-bg);
      color: var(--ok-text);
    }

    &.is-deny {
      background: var(--danger-bg);
      color: var(--danger);
    }
  }

  &__scope {
    font-size: 11px;
    color: var(--ink-400);
  }

  &__meta {
    font-size: 11px;
    color: var(--ink-400);
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  &__del {
    border: 0;
    background: transparent;
    color: var(--ink-300);
    cursor: pointer;
    width: 22px;
    height: 22px;
    border-radius: 4px;

    &:hover {
      background: var(--danger-bg);
      color: var(--danger);
    }
  }
}

/* ---------- 审计 ---------- */
.audit-filter {
  display: flex;
  gap: 8px;
}

.filter-input {
  width: 220px;
}

.filter-days {
  width: 100px;
}

.m-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--ink-100);

  > span {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.link-btn {
  border: 0;
  background: transparent;
  color: var(--rose-600);
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
}

.btn {
  height: 32px;
  padding: 0 14px;
  border-radius: var(--r-sm);
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink-600);
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    border-color: var(--ink-300);
    color: var(--ink-900);
  }

  &--primary {
    background: var(--rose-500);
    border-color: var(--rose-500);
    color: #fff;
    font-weight: 500;

    &:hover {
      background: var(--rose-600);
      border-color: var(--rose-600);
      color: #fff;
    }
  }
}

/* ---------- 抽屉 ---------- */
.drawer-body {
  padding: 22px 24px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink-100);
  flex-shrink: 0;

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);
    word-break: break-all;
  }

  p {
    margin-top: 6px;
    font-size: 12px;
    color: var(--ink-400);
  }
}

.drawer-close {
  border: 0;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: var(--r-sm);
  color: var(--ink-400);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--ink-100);
    color: var(--ink-900);
  }
}

.detail-block {
  margin-top: 16px;

  &__label {
    font-size: 12px;
    color: var(--ink-400);
    margin-bottom: 7px;
  }

  &__code {
    padding: 12px 14px;
    background: var(--ink-100);
    border-radius: var(--r-md);
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    color: var(--ink-700);
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 320px;
    overflow-y: auto;
  }
}

.detail-meta {
  display: flex;
  gap: 18px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--ink-100);

  span {
    font-size: 12px;
    color: var(--ink-400);
    font-variant-numeric: tabular-nums;
  }
}

.form-hint {
  margin-top: 4px;
  font-size: 11px;
  color: var(--ink-400);
}

.dialog-tip {
  font-size: 11px;
  color: var(--ink-400);
  line-height: 1.8;
}

@media (max-width: 1200px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .m-cols {
    grid-template-columns: 1fr;
  }
}
</style>
